import React, { useEffect, useState } from "react";
import { Button, Modal, Offcanvas } from "react-bootstrap";
import { IoMdAddCircle } from "react-icons/io";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import ReactSelect from "react-select";
import { FaRegEdit } from "react-icons/fa";
import axios from "axios";
import {
  addCustomer,
  updateCustomer,
} from "../../../redux/reducers/customerSlice";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../../components/Spinner";
import PhoneInput from "../../../components/common/PhoneInput";

export default function AddCustomer({ type, data }) {
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const dispatch = useDispatch();
  const selectedBusiness = useSelector(
    (state) => state.business.selectedBusiness
  );
  const { loading } = useSelector((state) => state.customer);

  const [currencydata, setCurrencydata] = useState([]);
  const [countrydata, setCountrydata] = useState([]);
  const [countrystatedata, setCountrystatedata] = useState([]);

  useEffect(() => {
    getCurrencydata();
    getCountrydata();
  }, []);

  const getCurrencydata = async () => {
    try {
      const response = await axios.get("https://restcountries.com/v3.1/all?fields=currencies");
      const currencies = [];
      response.data.forEach(country => {
        if (country.currencies) {
          Object.keys(country.currencies).forEach(code => {
            if (!currencies.find(c => c.code === code)) {
              currencies.push({ code, name: country.currencies[code].name });
            }
          });
        }
      });
      setCurrencydata(currencies.sort((a, b) => a.code.localeCompare(b.code)));
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  };

  const getCountrydata = async () => {
    try {
      const response = await axios.get("https://restcountries.com/v3.1/all?fields=name");
      const countries = response.data.map(c => ({ name: c.name.common }));
      setCountrydata(countries.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const getStatedata = async ({ countryname }) => {
    try {
      const response = await axios.post("https://countriesnow.space/api/v0.1/countries/states", {
        country: countryname
      });
      setCountrystatedata(response.data.data.states || []);
    } catch (error) {
      console.error("Error fetching states:", error);
      setCountrystatedata([]);
    }
  };

  useEffect(() => {
    if (type === "update" && data?.address?.country) {
      getStatedata({ countryname: data.address.country });
    }
  }, [type, data]);

  const initialValues = {
    id: type === "update" ? data?._id : "",
    name: type === "update" ? data?.name : "",
    email: type === "update" ? data?.email : "",
    phone: type === "update" 
      ? (typeof data?.phone === 'string' ? { countryCode: '+1', number: data.phone } : data?.phone || { countryCode: '+1', number: "" })
      : { countryCode: '+1', number: "" },
    currency: type === "update" ? data?.currency : "USD",
    businessId: selectedBusiness?._id,
    address: {
      street: type === "update" ? data?.address?.street : "",
      city: type === "update" ? data?.address?.city : "",
      state: type === "update" ? data?.address?.state : "",
      country: type === "update" ? data?.address?.country : "",
      zipCode: type === "update" ? data?.address?.zipCode : "",
    },
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Customer Name Required"),
    email: Yup.string().email("Invalid email").required("Email Required"),
    currency: Yup.string().required("Currency Required"),
  });

  const onSubmit = (values) => {
    if (type === "add") {
      dispatch(addCustomer(values)).then((res) => {
        if (!res.error) handleClose();
      });
    } else {
      dispatch(updateCustomer(values)).then((res) => {
        if (!res.error) handleClose();
      });
    }
  };

  return (
    <div className="d-inline-block">
      <div onClick={handleShow} style={{ cursor: 'pointer' }}>
        {type === "add" ? (
          <button className="btn btn-primary d-flex align-items-center gap-2">
            <IoMdAddCircle size={20} /> New Customer
          </button>
        ) : (
          <div className="action-icon-edit text-primary p-2 rounded-circle bg-light d-flex align-items-center justify-content-center" title="Edit Customer">
            <FaRegEdit size={16} />
          </div>
        )}
      </div>

      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        className="premium-modal"
        backdrop="static"
      >
        <Modal.Header className="border-0 pb-0 pt-4 px-4 position-relative">
          <Modal.Title className="fw-bold text-dark fs-4">
            {type === "add" ? "Create New Customer" : "Update Customer Details"}
          </Modal.Title>
          <button
            type="button"
            className="btn-close position-absolute top-0 end-0 m-3 p-2 bg-light rounded-circle shadow-sm"
            onClick={loading ? null : handleClose}
            disabled={loading}
            aria-label="Close"
          ></button>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Spinner active={loading}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              enableReinitialize={true}
            >
              {(formik) => (
                <Form>
                  <div className="row g-4">
                    {/* Section: Personal Info */}
                    <div className="col-12 px-2 pb-2 border-bottom mb-2">
                      <h6 className="text-uppercase text-muted fw-bold small mb-0">Personal Information</h6>
                    </div>

                    <div className="col-md-6 text-start">
                      <label className="invoice-label">Name <span className="text-danger">*</span></label>
                      <Field
                        type="text"
                        name="name"
                        className="gbl-input w-100"
                        placeholder="e.g. John"
                      />
                      <ErrorMessage name="name" component="div" className="formik_red_txt mt-1 small" />
                    </div>

                    <div className="col-md-6 text-start">
                      <label className="invoice-label">Email Address <span className="text-danger">*</span></label>
                      <Field
                        type="email"
                        name="email"
                        className="gbl-input w-100"
                        placeholder="john@example.com"
                      />
                      <ErrorMessage name="email" component="div" className="formik_red_txt mt-1 small" />
                    </div>

                    <div className="col-md-6 text-start">
                      <PhoneInput
                        name="phone"
                        label="Phone Number"
                        value={formik.values.phone}
                        onChange={(val) => formik.setFieldValue('phone', val)}
                        error={formik.errors.phone?.number}
                        touched={formik.touched.phone?.number}
                      />
                    </div>

                    <div className="col-md-6 text-start">
                      <label className="invoice-label">Default Currency <span className="text-danger">*</span></label>
                      <Field name="currency">
                        {({ field, form: { setFieldValue } }) => (
                          <ReactSelect
                            options={currencydata?.map((e) => ({
                              label: e?.code,
                              value: e?.code,
                            }))}
                            placeholder="Search Currency..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                            value={currencydata?.filter(e => e.code === field.value).map(e => ({ label: e.code, value: e.code }))[0]}
                            isSearchable
                            onChange={(option) => setFieldValue(field.name, option.value)}
                          />
                        )}
                      </Field>
                    </div>

                    {/* Section: Address Details */}
                    <div className="col-12 px-2 pb-2 border-bottom mb-2 mt-4">
                      <h6 className="text-uppercase text-muted fw-bold small mb-0">Address Details</h6>
                    </div>

                    <div className="col-md-6 text-start">
                      <label className="invoice-label">Country </label>
                      <Field name="address.country">
                        {({ field, form: { setFieldValue } }) => (
                          <ReactSelect
                            options={countrydata?.map((e) => ({
                              label: e?.name,
                              value: e?.name,
                            }))}
                            placeholder="Select Country"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            value={countrydata?.filter(e => e.name === field.value).map(e => ({ label: e.name, value: e.name }))[0]}
                            isSearchable
                            onChange={(option) => {
                              setFieldValue(field.name, option.value);
                              getStatedata({ countryname: option.value });
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="address.country" component="div" className="formik_red_txt mt-1 small" />
                    </div>

                    <div className="col-md-6 text-start">
                      <label className="invoice-label">State / Province </label>
                      <Field name="address.state">
                        {({ field, form: { setFieldValue } }) => (
                          <ReactSelect
                            options={countrystatedata?.map((e) => ({
                              label: e?.name,
                              value: e?.name, // Keeping consistency with name
                            }))}
                            placeholder="Select State"
                            isDisabled={!formik?.values?.address?.country}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            value={countrystatedata?.filter(e => e.name === field.value).map(e => ({ label: e.name, value: e.name }))[0]}
                            isSearchable
                            onChange={(option) => setFieldValue(field.name, option.value)}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="address.state" component="div" className="formik_red_txt mt-1 small" />
                    </div>

                    <div className="col-md-6 text-start">
                      <label className="invoice-label">City </label>
                      <Field
                        type="text"
                        name="address.city"
                        className="gbl-input w-100"
                        placeholder="e.g. New York"
                      />
                      <ErrorMessage name="address.city" component="div" className="formik_red_txt mt-1 small" />
                    </div>

                    <div className="col-md-6 text-start">
                      <label className="invoice-label">Street Address </label>
                      <Field
                        type="text"
                        name="address.street"
                        className="gbl-input w-100"
                        placeholder="123 Business Way"
                      />
                      <ErrorMessage name="address.street" component="div" className="formik_red_txt mt-1 small" />
                    </div>

                    <div className="col-md-6 text-start mb-4">
                      <label className="invoice-label">Zip Code </label>
                      <Field
                        type="text"
                        name="address.zipCode"
                        className="gbl-input w-100"
                        placeholder="10001"
                      />
                      <ErrorMessage name="address.zipCode" component="div" className="formik_red_txt mt-1 small" />
                    </div>

                    <div className="col-12 mt-4 pt-3 border-top">
                      <div className="d-flex justify-content-end gap-3">
                        <button
                          type="button"
                          className="btn btn-link text-muted text-decoration-none fw-bold"
                          onClick={handleClose}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary rounded-pill px-5 fw-bold shadow-sm"
                          disabled={loading}
                        >
                          {type === "add" ? `Create Customer` : `Save Changes`}
                        </button>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </Spinner>
        </Modal.Body>
      </Modal>
    </div>
  );
}
